import { openDB, type DBSchema, type IDBPDatabase, type IDBPTransaction } from 'idb';
import { MIGRATIONS, SCHEMA_VERSION, runMigrations } from './migrations';
import type {
  Abbrev,
  CalibrationRun,
  CandidatePhrase,
  Category,
  DiagnosticsEntry,
  HistoryEntry,
  Meta,
  PersonalBigram,
  PersonalUnigram,
  Phrase,
  QuickFire,
  Settings,
} from '../core/types';

/**
 * IndexedDB is the only durable store in this app (INV-04).
 * `localStorage` is never used: ITP purges script-created Web Storage after seven
 * days without interaction, and low disk space evicts it like cache. Binaries
 * (synthesised audio, later Piper models) live in the Cache API instead.
 *
 * The corpus lexicon deliberately does **not** live here. §6's data model listed `lexicon` and
 * `bigrams` as stores, but §5.3 needs a trie ready inside 200 ms and 50 000 rows of IndexedDB cannot
 * deliver that — so the packed binary in `public/lexicon/` is the real design, and v2 drops the stores
 * (C40). Personal n-grams, which are the part that must survive, stay here.
 *
 * Only repositories may import this module. Surfaces and components must not
 * (the layering rule, CON-10) — a component that knows a store name is
 * a component that will one day be the reason a migration cannot happen.
 */

export const DB_NAME = 'sags';

export interface SagsDb extends DBSchema {
  settings: { key: 'settings'; value: Settings };
  meta: { key: 'meta'; value: Meta };
  quickFires: { key: string; value: QuickFire; indexes: { byOrder: number } };
  categories: { key: string; value: Category; indexes: { byOrder: number } };
  phrases: {
    key: string;
    value: Phrase;
    indexes: { byCategory: string; byOrder: number };
  };
  abbrevs: { key: string; value: Abbrev };
  personalUnigrams: { key: string; value: PersonalUnigram };
  personalBigrams: { key: [string, string]; value: PersonalBigram };
  candidatePhrases: { key: string; value: CandidatePhrase };
  history: { key: string; value: HistoryEntry; indexes: { bySpokenAt: string } };
  calibration: { key: string; value: CalibrationRun; indexes: { byRanAt: string } };
  diagnostics: {
    key: string;
    value: DiagnosticsEntry;
    indexes: { byTs: string; byType: string };
  };
}

/** Every store, in one place, so export/import and wipe cannot silently miss one. */
export const STORE_NAMES = [
  'settings',
  'meta',
  'quickFires',
  'categories',
  'phrases',
  'abbrevs',
  'personalUnigrams',
  'personalBigrams',
  'candidatePhrases',
  'history',
  'calibration',
  'diagnostics',
] as const satisfies readonly (keyof SagsDb)[];

export type StoreName = (typeof STORE_NAMES)[number];

let dbPromise: Promise<IDBPDatabase<SagsDb>> | null = null;

export function getDb(): Promise<IDBPDatabase<SagsDb>> {
  dbPromise ??= openDB<SagsDb>(DB_NAME, SCHEMA_VERSION, {
    async upgrade(db, oldVersion, newVersion, tx) {
      // Awaited: v2 onwards read and write rows, and the versionchange transaction stays open while
      // its requests are pending.
      await runMigrations(MIGRATIONS, { db, tx, oldVersion, newVersion: newVersion ?? SCHEMA_VERSION });
    },
    blocked() {
      // Another tab holds an old version open. In an installed single-window PWA
      // this should not happen; log it rather than hang silently.
      console.warn('[db] upgrade blocked by another connection');
    },
    blocking() {
      console.warn('[db] this connection is blocking a newer version');
    },
    terminated() {
      // Safari can drop the connection (backgrounding, memory pressure). Forget the
      // cached promise so the next call reopens instead of using a dead handle.
      console.warn('[db] connection terminated, will reopen on next use');
      dbPromise = null;
    },
  });
  return dbPromise;
}

/**
 * Stores holding facts about the **device** rather than about the person.
 *
 * The distinction earns its keep at restore time. `meta` carries the install date, the storage-persistence
 * result and — from Phase 4 — the backup token and encryption key, and none of that is in an export
 * because none of it describes the person. So an import that cleared it would destroy the credentials
 * required by the very next backup, on a device that had just been restored *from* a backup. Since
 * failed backups are silent by design, the device would look restored and quietly stop protecting itself
 * until the seven-day escalation noticed (C52).
 */
const DEVICE_STORES: readonly StoreName[] = ['meta'];

export interface ClearOptions {
  /**
   * Wipe device facts too. `false` for an import — "make this device be that device" is a statement
   * about the person's data, not about which device it is.
   */
  includeDeviceStores: boolean;
}

/**
 * Delete rows in every store. Used by import (which replaces state wholesale)
 * and by the PIN-gated wipe in Betreuer-Modus. Deliberately not exported to
 * surfaces — it goes through a repository.
 *
 * Clearing device stores is the **default**, so the `wipeData` gated action gets the right behaviour when
 * it grows a caller: "delete everything" from a caregiver means the device stops being this person's
 * device, credentials included. Import is the one case that opts out.
 */
/**
 * The stores that describe the person. Everything but the device facts.
 *
 * Derived rather than listed, because a list is how `applyImport` came to name ten stores while the
 * clear beside it named eleven (C74). It is also the transaction scope of a restore — see
 * `replacePersonStores` — and an IndexedDB scope is fixed when the transaction is created.
 */
export const PERSON_STORES: readonly StoreName[] = STORE_NAMES.filter(
  (name) => !DEVICE_STORES.includes(name),
);

export async function clearAllStores(
  options: ClearOptions = { includeDeviceStores: true },
): Promise<void> {
  const db = await getDb();
  const names = options.includeDeviceStores ? STORE_NAMES : PERSON_STORES;

  const tx = db.transaction(names, 'readwrite');
  await Promise.all(names.map((name) => tx.objectStore(name).clear()));
  await tx.done;
}

/**
 * Clear the person's stores and write their replacement in **one** transaction, or leave the device
 * exactly as it was.
 *
 * ── Why one transaction (C74) ──
 *
 * The clear used to be its own committed transaction, with the writes in a second one. That made a
 * failed restore worse than a refused one, in two different ways. A `TypeError` thrown from a loop
 * *header* left no pending request, so the write transaction **auto-committed what it already had** —
 * a half-written device that looked plausible. A rejected `put` **aborted** it, leaving the device
 * empty. Neither could be undone, because the clear had already committed.
 *
 * The explicit `abort()` is the load-bearing half: a transaction with nothing pending commits, so
 * catching the error is not enough on its own. With this, a failure nothing could have predicted —
 * quota, a connection Safari terminated, a value structured clone refuses — leaves the old device
 * standing, and the German sentence saying so is a fact rather than a hope.
 */
export async function replacePersonStores(
  write: (tx: IDBPTransaction<SagsDb, readonly StoreName[], 'readwrite'>) => Promise<void>,
): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(PERSON_STORES, 'readwrite');

  try {
    await Promise.all(PERSON_STORES.map((name) => tx.objectStore(name).clear()));
    await write(tx);
    await tx.done;
  } catch (err) {
    try {
      tx.abort();
    } catch {
      // Already settled — the engine aborted it for us, which is the same outcome.
    }
    // The abort rejects `tx.done` as well; the caller needs the original reason, not `AbortError`.
    await tx.done.catch(() => {});
    throw err;
  }
}

/**
 * Close the connection and forget it. The next `getDb()` reopens.
 *
 * Needed for real: an open connection blocks `deleteDB` and blocks a version
 * upgrade, so the PIN-gated wipe and the test harness both have to be able to let
 * go of the handle.
 */
export async function closeDb(): Promise<void> {
  if (!dbPromise) return;
  const pending = dbPromise;
  dbPromise = null;
  try {
    (await pending).close();
  } catch (err) {
    console.warn('[db] close failed', err);
  }
}
